import { Routes, Route, Navigate } from 'react-router-dom';
import { useATSRole } from './hooks/useATSRole';
import { registerAtsNamespace } from './i18n/register';
import { AtsLayout } from './layouts/AtsLayout';
import { AtsLayoutWide } from './layouts/AtsLayoutWide';
import { PoolListPage } from './pages/pools/PoolListPage';
import { PoolDetailPage } from './pages/pools/PoolDetailPage';
import { PositionListPage } from './pages/positions/PositionListPage';
import { PositionDetailPage } from './pages/positions/PositionDetailPage';
import { CampaignListPage } from './pages/campaigns/CampaignListPage';
import { CampaignDetailPage } from './pages/campaigns/CampaignDetailPage';
import { PipelineListPage } from './pages/pipelines/PipelineListPage';
import { PipelineDetailPage } from './pages/pipelines/PipelineDetailPage';
import { JobListPage } from './pages/jobs/JobListPage';
import { JobDetailPage } from './pages/jobs/JobDetailPage';
import { CVListPage } from './pages/cvs/CVListPage';
import { CVDetailPage } from './pages/cvs/CVDetailPage';
import { RolesPage } from './pages/roles/RolesPage';
import { SourceListPage } from './pages/sources/SourceListPage';
import { RejectionReasonListPage } from './pages/rejection-reasons/RejectionReasonListPage';
import { EmployeeTypeListPage } from './pages/employee-types/EmployeeTypeListPage';
import { RecruitmentTypeListPage } from './pages/recruitment-types/RecruitmentTypeListPage';
import { WorkArrangementListPage } from './pages/work-arrangements/WorkArrangementListPage';
import { PublicSharePage } from './pages/public/PublicSharePage';
import { PublicCVDetailPage } from './pages/public/PublicCVDetailPage';
import { JobDescriptionPage } from './pages/job-descriptions/JobDescriptionPage';
import { JDDetailPage } from './pages/job-descriptions/JDDetailPage';
import { JDListingPage } from './pages/job-descriptions/JDListingPage';
import { CareersRedirect } from './pages/careers/CareersRedirect';
import { CalendarPage } from './pages/calendar/CalendarPage';
import { DashboardPage } from './pages/dashboard/DashboardPage';
import { EventTypeConfigPage } from './pages/event-types/EventTypeConfigPage';
import { EmailTemplateConfigPage } from './pages/email-templates/EmailTemplateConfigPage';
import { EmailSignatureConfigPage } from './pages/email-signatures/EmailSignatureConfigPage';
import { EmailListPage } from './pages/emails/EmailListPage';
import { EmailDetailPage } from './pages/emails/EmailDetailPage';

// Register ATS i18n namespace as a side-effect of importing the routes.
// Shell imports AtsRoutes as a library, so ats/main.tsx never runs —
// without this, `useTranslation('ats')` would receive raw keys.
registerAtsNamespace();

function LoadingSpinner() {
  return (
    <div className="flex justify-center items-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
    </div>
  );
}

/**
 * Route guard: render page only for leader/manager, redirect others.
 * Shows loading spinner while role is being fetched.
 */
function LeaderRoute({ children, isLoading, isLeaderOrAbove }: {
  children: React.ReactNode;
  isLoading: boolean;
  isLeaderOrAbove: boolean;
}) {
  if (isLoading) return <LoadingSpinner />;
  if (!isLeaderOrAbove) return <Navigate to="/ats/pools" replace />;
  return <>{children}</>;
}

export function AtsRoutes() {
  const { isLoading, isLeaderOrAbove } = useATSRole();

  return (
    <Routes>
      {/* Public routes - no authentication required, no layout */}
      <Route path="/share/:token" element={<PublicSharePage />} />
      <Route path="/share/:token/:cvId" element={<PublicCVDetailPage />} />
      <Route path="/careers/:workspaceCode/*" element={<CareersRedirect />} />
      {/* Wide layout routes - for pages that need full width */}
      <Route
        path="/emails"
        element={
          <AtsLayoutWide>
            <EmailListPage />
          </AtsLayoutWide>
        }
      />
      <Route
        path="/emails/:threadId"
        element={
          <AtsLayoutWide>
            <EmailDetailPage />
          </AtsLayoutWide>
        }
      />
      <Route
        path="/email-signatures"
        element={
          <AtsLayoutWide>
            <EmailSignatureConfigPage />
          </AtsLayoutWide>
        }
      />
      <Route
        path="/jobs/:jobId"
        element={
          <AtsLayoutWide>
            <JobDetailPage />
          </AtsLayoutWide>
        }
      />

      {/* Job Descriptions - global listing */}
      <Route
        path="/job-descriptions"
        element={
          <AtsLayout>
            <JDListingPage />
          </AtsLayout>
        }
      />

      {/* Job Descriptions - per-job listing */}
      <Route
        path="/jobs/:jobId/job-descriptions"
        element={
          <AtsLayout>
            <JobDescriptionPage />
          </AtsLayout>
        }
      />

      {/* Job Description detail - flat path */}
      <Route
        path="/job-descriptions/:jdId"
        element={
          <AtsLayout>
            <JDDetailPage />
          </AtsLayout>
        }
      />

      {/* Standard layout routes */}
      <Route
        path="/*"
        element={
          <AtsLayout>
            <Routes>
              {/* Redirect root to Dashboard */}
              <Route path="/" element={<Navigate to="/ats/dashboard" replace />} />

              {/* Dashboard */}
              <Route path="/dashboard" element={<DashboardPage />} />

              {/* Calendar */}
              <Route path="/calendar" element={<CalendarPage />} />

              {/* Pools */}
              <Route path="/pools" element={<PoolListPage />} />
              <Route path="/pools/:poolId" element={<PoolDetailPage />} />

              {/* Positions */}
              <Route path="/positions" element={<LeaderRoute isLoading={isLoading} isLeaderOrAbove={isLeaderOrAbove}><PositionListPage /></LeaderRoute>} />
              <Route path="/positions/:positionId" element={<LeaderRoute isLoading={isLoading} isLeaderOrAbove={isLeaderOrAbove}><PositionDetailPage /></LeaderRoute>} />

              {/* Campaigns */}
              <Route path="/campaigns" element={<LeaderRoute isLoading={isLoading} isLeaderOrAbove={isLeaderOrAbove}><CampaignListPage /></LeaderRoute>} />
              <Route path="/campaigns/:campaignId" element={<LeaderRoute isLoading={isLoading} isLeaderOrAbove={isLeaderOrAbove}><CampaignDetailPage /></LeaderRoute>} />

              {/* Pipelines */}
              <Route path="/pipelines" element={<LeaderRoute isLoading={isLoading} isLeaderOrAbove={isLeaderOrAbove}><PipelineListPage /></LeaderRoute>} />
              <Route path="/pipelines/:pipelineId" element={<LeaderRoute isLoading={isLoading} isLeaderOrAbove={isLeaderOrAbove}><PipelineDetailPage /></LeaderRoute>} />

              {/* Jobs */}
              <Route path="/jobs" element={<JobListPage />} />

              {/* CVs */}
              <Route path="/cvs" element={<CVListPage />} />
              <Route path="/cvs/:cvId" element={<CVDetailPage />} />

              {/* Roles */}
              <Route
                path="/roles"
                element={<LeaderRoute isLoading={isLoading} isLeaderOrAbove={isLeaderOrAbove}><RolesPage /></LeaderRoute>}
              />

              {/* Reference Data */}
              <Route path="/sources" element={<LeaderRoute isLoading={isLoading} isLeaderOrAbove={isLeaderOrAbove}><SourceListPage /></LeaderRoute>} />
              <Route path="/rejection-reasons" element={<LeaderRoute isLoading={isLoading} isLeaderOrAbove={isLeaderOrAbove}><RejectionReasonListPage /></LeaderRoute>} />
              <Route path="/employee-types" element={<LeaderRoute isLoading={isLoading} isLeaderOrAbove={isLeaderOrAbove}><EmployeeTypeListPage /></LeaderRoute>} />
              <Route path="/recruitment-types" element={<LeaderRoute isLoading={isLoading} isLeaderOrAbove={isLeaderOrAbove}><RecruitmentTypeListPage /></LeaderRoute>} />
              <Route path="/work-arrangements" element={<LeaderRoute isLoading={isLoading} isLeaderOrAbove={isLeaderOrAbove}><WorkArrangementListPage /></LeaderRoute>} />
              <Route path="/event-types" element={<LeaderRoute isLoading={isLoading} isLeaderOrAbove={isLeaderOrAbove}><EventTypeConfigPage /></LeaderRoute>} />
              <Route path="/email-templates" element={<LeaderRoute isLoading={isLoading} isLeaderOrAbove={isLeaderOrAbove}><EmailTemplateConfigPage /></LeaderRoute>} />
            </Routes>
          </AtsLayout>
        }
      />
    </Routes>
  );
}
